import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {action, makeAutoObservable, observable} from "mobx";
import {ModalType, PlayedGames, RequestState} from "data/enums";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IUserStore} from "data/stores/user/user.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {includes, every, some, eq} from "lodash";
import stepImg_0 from "assets/img/rc_fantasy_logo.svg";
import stepImg_1 from "assets/img/tutorial/tutorial_step_1_UNCOMPRESSED.jpg";
import stepImg_2 from "assets/img/tutorial/tutorial_step_2_UNCOMPRESSED.jpg";
import stepImg_3 from "assets/img/tutorial/tutorial_step_3_UNCOMPRESSED.jpg";
import stepImg_4 from "assets/img/tutorial/tutorial_step_4_UNCOMPRESSED.jpg";
import stepImgPrize from "assets/img/tutorial/tutorial_step_5_UNCOMPRESSED.jpg";
import stepImg_5 from "assets/img/icon/icon-checked.svg";

export interface IStep {
	image: string;
	title: string;
	description: string;
}

export interface ITutorialModalController extends ViewController {
	get i18n(): ILocalizationStore;
	get visibleModal(): boolean;
	get currentStep(): number;
	get isFirstStep(): boolean;
	get isLastStep(): boolean;
	get currentStepData(): IStep;
	get stepsArr(): IStep[];

	toNextStep: () => void;
	toPrevStep: () => void;
	toFirstStep: () => void;
	onFinishTutorial: () => void;
}

@injectable()
export class TutorialModalController implements ITutorialModalController {
	@observable _requestState: RequestState = RequestState.IDLE;
	@observable _currentStep: number = 0;
	@observable _stepsArr: IStep[] = [
		{
			image: stepImg_0,
			title: this.i18n.t("modal.tutorial.step_0_title", "Welcome"),
			description: this.i18n.t("modal.tutorial.step_0_description", "Copy TBC 0"),
		},
		{
			image: stepImg_1,
			title: this.i18n.t(
				"modal.tutorial.step_1_title",
				"PICK YOUR TEAM EACH DAY OF THE TOURNAMENT"
			),
			description: this.i18n.t(
				"modal.tutorial.step_1_description",
				"Update picks as much as you'd like before the first tee time of the day."
			),
		},
		{
			image: stepImg_2,
			title: this.i18n.t("modal.tutorial.step_2_title", "CHOOSE YOUR CAPTAIN"),
			description: this.i18n.t(
				"modal.tutorial.step_2_description",
				"Start earning 1.5X points by selecting a Captain."
			),
		},
		{
			image: stepImg_3,
			title: this.i18n.t("modal.tutorial.step_3_title", "ENHANCE YOR POINTS"),
			description: this.i18n.t(
				"modal.tutorial.step_3_description",
				"Don't forget to use your three transfers for the afternoon sessions on Friday and Saturday to maximise your points. Transfers can be made any time between the morning session starting and the first tee time in the afternoon"
			),
		},
		{
			image: stepImg_4,
			title: this.i18n.t("modal.tutorial.step_4_title", "PLAY WITH FRIENDS"),
			description: this.i18n.t(
				"modal.tutorial.step_4_description",
				"Join a league and share on socials."
			),
		},
		{
			image: stepImgPrize,
			title: this.i18n.t("modal.tutorial.step_prize_title", "WIN INCREDIBLE PRIZES!"),
			description: this.i18n.t(
				"modal.tutorial.step_prize_description",
				"Be in with a chance to win a trip to New York for the 2025 Ryder Cup!"
			),
		},
		{
			image: stepImg_5,
			title: this.i18n.t("modal.tutorial.step_5_title", "YOU'RE READY!"),
			description: this.i18n.t(
				"modal.tutorial.step_5_description",
				"Close the modal to start the game now!"
			),
		},
	];

	constructor(
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.ModalsStore) private _modalStore: IModalsStore
	) {
		makeAutoObservable(this);
	}

	get i18n() {
		return this._i18nStore;
	}

	get visibleModal() {
		const autoVisible = every([
			includes(this._userStore.user?.playedGames, PlayedGames.Fantasy),
			!this._userStore.user?.tutorial,
		]);
		return some([autoVisible, eq(this._modalStore.modal, ModalType.TUTORIAL)]);
	}

	get currentStep() {
		return this._currentStep;
	}

	get isFirstStep() {
		return eq(this.currentStep, 0);
	}

	get isLastStep() {
		return eq(this.currentStep + 1, this._stepsArr.length);
	}

	get currentStepData() {
		return this._stepsArr[this.currentStep];
	}

	get stepsArr() {
		return this._stepsArr;
	}

	@action toNextStep = () => {
		this._currentStep = this.currentStep + 1;
	};

	@action toPrevStep = () => {
		this._currentStep = this.currentStep - 1;
	};

	@action toFirstStep = () => {
		this._currentStep = 0;
	};

	@action onFinishTutorial = () => {
		setTimeout(() => {
			this.toFirstStep();
		}, 1000);

		if (!this._userStore.user?.tutorial) {
			void this._userStore.update({tutorial: true});
		} else {
			this._modalStore.hideModal();
		}
	};

	dispose() {
		return;
	}

	init(param: void) {
		return;
	}
}
